import { useBetSlipStore } from '../../useBetSlip';
import { BetTypeSwitch } from './BetTypeSwitch';
import { OddsSettingPage } from './OddsSetting';
import { ActionLocked, LockedKey } from './ActionLocked';
import { BetItems } from './BetItems';
import { BetActionArea } from './BetActionArea';
import { PortalRoot } from './Portal';
import { PlaceBetStatusPop } from './placeBetStatus.pop';
import { NegativeEquity } from './NegativeEquity';
import { BetStatus } from '../../types';
import '../css.css';

/**
 * BetSlip Tab 内容组件
 * 包含投注类型切换、赔率设置、投注项列表和投注操作区域
 */
export const BetSlipContent = () => {
  const [store, dispatch] = useBetSlipStore();
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = createSignal(false);

  let scrollContainerRef: HTMLDivElement | undefined;
  let prevOddsLength = store.odds.length;
  let resizeObserver: ResizeObserver | undefined;

  const updateScrollState = () => {
    if (!scrollContainerRef) return;
    setHasVerticalScrollbar(scrollContainerRef.scrollHeight > scrollContainerRef.clientHeight);
  };

  createEffect(
    on(
      () => store.odds.length,
      (currentLength) => {
        if (currentLength > prevOddsLength && scrollContainerRef) {
          // 先立即跳到底部附近（不平滑），为动画做准备
          requestAnimationFrame(() => {
            if (!scrollContainerRef) return;

            // 平滑滚动到底部，配合新 item 的上推动画
            scrollContainerRef.scrollTo({
              top: scrollContainerRef.scrollHeight,
              behavior: 'smooth',
            });
            updateScrollState();
          });
        }
        prevOddsLength = currentLength;
      }
    )
  );

  onMount(() => {
    if (!scrollContainerRef) return;

    updateScrollState();
    resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollContainerRef);

    scrollContainerRef.addEventListener('scroll', updateScrollState, { passive: true });
  });

  onCleanup(() => {
    resizeObserver?.disconnect();
    scrollContainerRef?.removeEventListener('scroll', updateScrollState);
  });

  return (
    <div class="flex flex-col flex-1 min-h-0">
      <div class="flex-shrink-0">
        {/* 投注类型切换器 */}
        <BetTypeSwitch
          value={store.betType}
          onChange={(betType) =>
            dispatch({
              type: 'SET_BET_TYPE',
              betType,
            })
          }
        />

        {/* 赔率设置下拉选择器 */}
        <Show when={store.odds.length > 0}>
          <div class="flex h-8 justify-between items-center bg-betslip-body-bg">
            <OddsSettingPage />

            {/* 清空所有投注按钮 */}
            <Button
              disabled={store.odds.length === 0}
              class="h-10 caption-m-medium text-neutral-main2"
              onClick={async () => {
                dispatch({
                  type: 'CLEAR_ALL_BETS',
                });
                // 清除后，手动还初始金额
                dispatch({
                  type: 'SET_COMBO_AMOUNT',
                  amount: 0,
                });
              }}
            >
              {t('Clear All')}
            </Button>
          </div>
        </Show>
      </div>

      {/* 投注内容区域 - 显示投注项列表 */}
      <div
        class={cx(
          'flex-1 overflow-y-auto bg-betslip-body-bg custom-scrollbar',
          store.quickBet ? 'min-h-0' : 'min-h-[206px]'
        )}
        ref={scrollContainerRef}
        style={{
          'scroll-behavior': 'smooth', // 平滑滚动
        }}
      >
        <ActionLocked lockedKey={LockedKey.bet_items}>
          <BetItems />
        </ActionLocked>
      </div>

      {/* 投注操作区域 - 包含金额输入、快速投注按钮和投注按钮 */}
      <div
        class={cx(
          'flex-shrink-0 relative z-[1]',
          hasVerticalScrollbar() && 'shadow-[0_-6px_16px_0_var(--effect-control-shadow,rgba(10,15,19,0.30))]'
        )}
      >
        <BetActionArea />
      </div>
      <PortalRoot id="bet-slip-portal" />
      <Show when={store.betStatus === BetStatus.SUCCESS}>
        <PlaceBetStatusPop status={store.betStatus} />
      </Show>
      {/* 如果是负资产的情况下，进行下注的时候，则需要一个提示 */}
      <NegativeEquity />
    </div>
  );
};
